import React, {ReactElement} from "react";
import {Box} from "@mui/material";
import {isFieldValid, isSchemaValid, PageStatus} from "ndr-designsystem";
import {useTranslation} from "react-i18next";
import {SectionRoot, SectionHeading, FieldGrid, TextInput, BooleanInput, ListInput} from "../../../Components/fields";
import SchemaConstants from "../../../utils/schemaConstants";
import {
    setAddress,
    setExPostRecipients,
    setAdminNotificationsRecipients,
    setMarketPartnerIDCode,
    setMarketPartnerIDEncoding,
    setName,
    setSubdomain,
    setUseAVAT,
} from "../store/TenantsSlice";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {EmailSchema, GeneralTabSchema} from "../../../validations/TenantDetailsValidation";

const GeneralTab = ({pageStatus}:{pageStatus: PageStatus}): ReactElement => {
    const dispatch = useAppDispatch();
    const isEditing = pageStatus === PageStatus.EDIT
    const isAdding = pageStatus === PageStatus.ADD
    const { currentTenant: tenant} = useAppSelector(state => state.Tenants)
    const {t} = useTranslation("tabels")
    const generalTabSchema = GeneralTabSchema(isAdding);

    return (
        <SectionRoot>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <Box>
                    <SectionHeading>{t("tenantDetails.tabs.general")}</SectionHeading>
                    <FieldGrid>
                        {!isAdding &&
                            <TextInput
                                label={t("tenantDetails.ID")}
                                value={tenant?.inventoryItemId}
                            />
                        }
                        <TextInput
                            label={t("tenantDetails.subdomain")}
                            value={tenant?.subdomain}
                            isEditing={isAdding}
                            required={isAdding}
                            onChange={(subdomain) => dispatch(setSubdomain(subdomain))}
                            checkValue={subdomain => isFieldValid(generalTabSchema, [SchemaConstants.subdomain], { subdomain })}
                        />
                        <TextInput
                            label={t("tenantDetails.name")}
                            value={tenant?.name}
                            isEditing={isEditing || isAdding}
                            required
                            onChange={(name) => dispatch(setName(name))}
                            checkValue={name => isFieldValid(generalTabSchema, [SchemaConstants.name], { name })}
                        />
                        <TextInput
                            label={t("tenantDetails.address")}
                            value={tenant?.address}
                            isEditing={isEditing || isAdding}
                            onChange={(address) => dispatch(setAddress(address))}
                            checkValue={address => isFieldValid(generalTabSchema, [SchemaConstants.address], { address })}
                        />
                        <TextInput
                            label={t("tenantDetails.marketPartnerIDCode")}
                            value={tenant?.marketPartnerIDCode}
                            isEditing={isAdding}
                            required={isAdding}
                            maxLength={13}
                            onChange={(marketPartnerIDCode) => dispatch(setMarketPartnerIDCode(marketPartnerIDCode))}
                            checkValue={marketPartnerIDCode => isFieldValid(generalTabSchema, [SchemaConstants.marketPartnerIDCode], { marketPartnerIDCode })}
                        />
                        <TextInput
                            label={t("tenantDetails.marketPartnerIDEncoding")}
                            value={tenant?.marketPartnerIDEncoding}
                            isEditing={isAdding}
                            required={isAdding}
                            onChange={(marketPartnerIDEncoding) => dispatch(setMarketPartnerIDEncoding(marketPartnerIDEncoding))}
                            checkValue={marketPartnerIDEncoding => isFieldValid(generalTabSchema, [SchemaConstants.marketPartnerIDEncoding], { marketPartnerIDEncoding })}
                        />
                        <BooleanInput
                            label={t("tenantDetails.UseAVAT")}
                            value={tenant?.useAvatForActivations}
                            isEditing={isEditing || isAdding}
                            onChange={(useAvat) => dispatch(setUseAVAT(useAvat))}
                        />
                        {!isAdding &&
                            <>
                                <TextInput
                                    label={t("tenantDetails.NumberOfControllableResources")}
                                    value={tenant?.controllableResourcesCount}
                                />
                                <TextInput
                                    label={t("tenantDetails.NumberOfActivations")}
                                    value={tenant?.activationsCount}
                                />
                            </>
                        }
                    </FieldGrid>
                </Box>

                <ListInput
                    title={t('tenantDetails.exPostRecipients')}
                    elements={tenant?.exPostRecipients}
                    isEditing={isEditing || isAdding}
                    onChange={(exPostRecipients) => dispatch(setExPostRecipients(exPostRecipients))}
                    checkValue={val => isSchemaValid(EmailSchema(), val as any)}
                    addButtonText={t("common:add_new")}
                    emptyText={t("common:no_records_text")}
                />

                <ListInput
                    title={t('tenantDetails.adminNotificationsRecipients')}
                    elements={tenant?.adminNotificationsRecipients}
                    isEditing={isEditing || isAdding}
                    onChange={(adminNotificationsRecipients) => dispatch(setAdminNotificationsRecipients(adminNotificationsRecipients))}
                    checkValue={val => isSchemaValid(EmailSchema(), val as any)}
                    addButtonText={t("common:add_new")}
                    emptyText={t("common:no_records_text")}
                />
            </Box>
        </SectionRoot>
    )
}

export default GeneralTab
